// Mieterportal (Portale E4): Datentypen der Portal-Funktionen und reine Anzeige-/Prüfregeln.
// Daten kommen ausschließlich über portal_mieter_kontext / portal_meine_meldungen; geschrieben wird
// nur über portal_schaden_melden (+ Fotos). Texte wie im Prototyp design_handoff_portale.
import { gesamtmieteAm, type MietverhaeltnisRoh } from '../mietkonto/soll';

export interface MieterKontext {
  mieter: {
    anrede: string | null; vorname: string | null; nachname: string | null; firma_name: string | null;
    mietbeginn: string | null; mietende: string | null;
    kaltmiete: number | null; nebenkosten_vorauszahlung: number | null; heizkosten: number | null; weitere_kosten: number | null;
    miet_historie: unknown; kaution: number | null; miete_faellig_tag: number | null; einheit_text: string | null;
    kaution_ist?: number | null; kaution_art?: string | null; zahlungsweise?: string | null;
  };
  einheit: { bezeichnung: string | null; etage: string | null; wohnflaeche: number | null; zimmer: number | null } | null;
  objekt: { strasse: string | null; hausnummer: string | null; plz: string | null; stadt: string | null } | null;
  ansprechpartner: { name: string | null; email: string | null } | null;
}

export interface Meldung {
  id: string; ticket_nummer: string | null; titel: string; status: string; prioritaet: string | null;
  created_at: string; updated_at: string | null; kategorie: string | null; ort: string | null; beschreibung: string | null;
  termin_start: string | null; termin_ende: string | null; termin_ganztaegig: boolean | null;
  /** #564 T3: Meldung oder Anfrage, Zahl der Portal-Nachrichten */
  art?: 'meldung' | 'anfrage'; nachrichten?: number;
  /** Anfrage: die Verwaltung hat im Portal geantwortet (kommt mit T6-SQL) */
  beantwortet?: boolean;
}

// ── Kategorien und Räume (wie portal_schaden_melden sie annimmt) ──
export type KategorieKey = 'heizung' | 'wasser' | 'strom' | 'tueren' | 'gemein' | 'sonst';
export const KATEGORIEN: { key: KategorieKey; label: string }[] = [
  { key: 'heizung', label: 'Heizung' },
  { key: 'wasser', label: 'Wasser & Sanitär' },
  { key: 'strom', label: 'Elektrik' },
  { key: 'tueren', label: 'Fenster & Türen' },
  { key: 'gemein', label: 'Gemeinschaft' },
  { key: 'sonst', label: 'Sonstiges' },
];
export const kategorieLabel = (k: KategorieKey): string => KATEGORIEN.find((x) => x.key === k)?.label ?? 'Sonstiges';

/** Kategorie einer Meldung aus dem Titel („Heizung · Bad“), wie das Portal ihn anlegt. */
export function kategorieAusTitel(titel: string): KategorieKey {
  const kopf = titel.split(' · ')[0]?.trim();
  return KATEGORIEN.find((k) => k.label === kopf)?.key ?? 'sonst';
}

export type Bereich = 'wohnung' | 'gemeinschaft';
export const RAEUME: Record<Bereich, string[]> = {
  wohnung: ['Bad', 'Küche', 'Wohnzimmer', 'Schlafzimmer', 'Kinderzimmer', 'Flur', 'Balkon', 'Ganze Wohnung'],
  gemeinschaft: ['Treppenhaus', 'Eingang', 'Keller', 'Waschkeller', 'Hof', 'Müllplatz'],
};

// ── Schaden melden: Pflichtfelder (die Datenbank prüft dieselben Regeln noch einmal) ──
export const MIN_BESCHREIBUNG = 10;
export const MAX_FOTOS = 8;
export const MAX_FOTO_BYTES = 10 * 1024 * 1024;

export interface MeldungEingabe { kategorie: KategorieKey | null; bereich: Bereich; raum: string | null; beschreibung: string }

export function pruefeMeldung(e: MeldungEingabe): { kategorie: boolean; beschreibung: boolean; ok: boolean } {
  const kategorie = !!e.kategorie;
  const beschreibung = e.beschreibung.trim().length >= MIN_BESCHREIBUNG;
  return { kategorie, beschreibung, ok: kategorie && beschreibung };
}

/** Raum gehört zum gewählten Bereich? Sonst wird er nicht mitgeschickt. */
export const raumGueltig = (bereich: Bereich, raum: string | null): boolean => !raum || RAEUME[bereich].includes(raum);

/** Foto annehmen? Gibt den Grund zurück, wenn nicht. */
export function fotoProblem(datei: { size: number; type: string; name: string }, schonDa: number): string | null {
  if (schonDa >= MAX_FOTOS) return `Höchstens ${MAX_FOTOS} Fotos je Meldung.`;
  const istBild = datei.type.startsWith('image/') || /\.(heic|heif|jpe?g|png|webp)$/i.test(datei.name);
  if (!istBild) return `„${datei.name}“ ist kein Foto.`;
  if (datei.size > MAX_FOTO_BYTES) return `„${datei.name}“ ist größer als 10 MB.`;
  return null;
}

/** Bildtyp für die Ablage – HEIC kommt aus manchen Browsern ohne Typ. */
export function fotoTyp(datei: { type: string; name: string }): string {
  if (datei.type) return datei.type;
  return /\.hei[cf]$/i.test(datei.name) ? 'image/heic' : 'image/jpeg';
}

// ── 4-Stufen-Status ──
export const STUFEN = ['Eingegangen', 'In Bearbeitung', 'Termin geplant', 'Erledigt'] as const;
export type Stufe = 0 | 1 | 2 | 3;

export function meldungStufe(m: Pick<Meldung, 'status' | 'termin_start'>): Stufe {
  if (m.status === 'erledigt' || m.status === 'geschlossen') return 3;
  if (m.termin_start) return 2;
  if (m.status === 'neu' || m.status === 'offen') return 0;
  return 1;
}

const DATUM = (iso: string) => new Date(iso).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
const UHR = (iso: string) => new Date(iso).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });

export function terminZeile(m: Pick<Meldung, 'termin_start' | 'termin_ende' | 'termin_ganztaegig'>): string {
  if (!m.termin_start) return '';
  const tag = new Date(m.termin_start).toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit' });
  if (m.termin_ganztaegig) return `${tag} (ganztägig)`;
  return m.termin_ende ? `${tag}, ${UHR(m.termin_start)}–${UHR(m.termin_ende)} Uhr` : `${tag}, ${UHR(m.termin_start)} Uhr`;
}

/** Was der Mieter als letzten Stand liest – nur aus echten Daten abgeleitet. */
export function meldungUpdate(m: Meldung): string {
  const s = meldungStufe(m);
  if (s === 3) return 'Erledigt';
  if (s === 2) return `Termin am ${terminZeile(m)}`;
  if (s === 1) return 'Die Verwaltung bearbeitet Ihre Meldung';
  return m.prioritaet === 'hoch' ? 'Als dringend markiert · wird geprüft' : 'Meldung eingegangen · wird geprüft';
}

export const meldungMeta = (m: Meldung): string =>
  [m.ticket_nummer, `gemeldet am ${DATUM(m.created_at)}`, m.ort?.split(' · ').pop()].filter(Boolean).join(' · ');

// ── Wohnung und Miete ──
const MONATE = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];

/** „Mieterin seit April 2021“ – Form aus der Anrede, sonst neutral. */
export function mieterSeit(anrede: string | null, mietbeginn: string | null): string {
  if (!mietbeginn) return '';
  const d = new Date(mietbeginn);
  if (Number.isNaN(d.getTime())) return '';
  const wann = `${MONATE[d.getMonth()]} ${d.getFullYear()}`;
  const a = (anrede ?? '').toLowerCase();
  if (/\bfrau\b/.test(a)) return `Mieterin seit ${wann}`;
  if (/\bherr\b/.test(a)) return `Mieter seit ${wann}`;
  return `Mietbeginn ${wann}`;
}

export function wohnungUntertitel(k: Pick<MieterKontext, 'einheit' | 'objekt' | 'mieter'>): string {
  const einheit = k.einheit?.bezeichnung || k.mieter.einheit_text;
  const ort = [k.objekt?.plz, k.objekt?.stadt].filter(Boolean).join(' ');
  return [einheit, k.einheit?.etage, ort].filter(Boolean).join(' · ');
}

export function warmmiete(m: MieterKontext['mieter'], heute: string): number {
  const roh: MietverhaeltnisRoh = {
    id: '', einheit_id: null, status: null, kaltmiete: m.kaltmiete, nebenkosten_vorauszahlung: m.nebenkosten_vorauszahlung,
    heizkosten: m.heizkosten, weitere_kosten: m.weitere_kosten, miet_historie: m.miet_historie,
    mietbeginn: m.mietbeginn, mietende: m.mietende,
  };
  return gesamtmieteAm(roh, heute);
}

/** Nächster Fälligkeitstag ab heute (Tag im Monat, Standard 3.; kurze Monate → letzter Tag). */
export function naechsteFaelligkeit(faelligTag: number | null, heute: Date): Date {
  const tag = faelligTag && faelligTag >= 1 && faelligTag <= 31 ? faelligTag : 3;
  const imMonat = (j: number, mo: number) => new Date(j, mo, Math.min(tag, new Date(j, mo + 1, 0).getDate()));
  const diesen = imMonat(heute.getFullYear(), heute.getMonth());
  const h = new Date(heute.getFullYear(), heute.getMonth(), heute.getDate());
  return diesen >= h ? diesen : imMonat(heute.getFullYear(), heute.getMonth() + 1);
}

export const euroGanz = (n: number): string => n.toLocaleString('de-DE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 });
export const euro = (n: number): string => n.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });

export const zahlText = (n: number | null | undefined): string | null =>
  n === null || n === undefined ? null : Number(n).toLocaleString('de-DE', { maximumFractionDigits: 1 });
